import {isSSOdMessage, type SSOdMessage} from '@pexip/infinity';

/** Why an SSO flow ended without a verdict. */
export type SsoAbandonReason =
    /** The user dismissed the identity provider surface. */
    | 'cancelled'
    /** The host could not run the flow through to a verdict. */
    | 'unavailable';

/** Outcome callbacks that a host transport reports for an SSO flow. */
export interface SsoFlowHandlers {
    /** Reports the identity provider's verdict exactly once. */
    onResult: (message: SSOdMessage) => void;
    /** Reports that the flow ended without a verdict. */
    onAbandon: (reason: SsoAbandonReason) => void;
}

/** Instructions for starting an SSO flow on the host's chosen surface. */
export interface SsoFlowOptions extends SsoFlowHandlers {
    /**
     * Identity provider URL, or an empty string when Infinity will supply it
     * later through its redirect signal.
     */
    redirectUrl: string;
    /**
     * Deployment asked to reuse the current view instead of a second surface.
     * Hosts that cannot navigate away may ignore it.
     */
    preferCurrentView: boolean;
}

/** An SSO flow that the host is currently running. */
export interface SsoFlow {
    /** Points the flow at a newly issued identity provider URL. */
    redirect: (redirectUrl: string) => void;
    /** Ends the flow and releases host resources. */
    close: () => void;
}

/** Runs SSO flows on the surface preferred by the host shell. */
export interface SsoTransport {
    /**
     * Starts an SSO flow.
     *
     * @param options - Identity provider URL, surface preference, and outcome
     * callbacks.
     * @returns The live flow, or `undefined` when the host refused to start it.
     *
     * @example
     * const flow = ssoTransport.start({
     *     redirectUrl: 'https://idp.example.com/authorize',
     *     preferCurrentView: false,
     *     onResult: handleSsoResult,
     *     onAbandon: returnToSignIn,
     * });
     */
    start: (options: SsoFlowOptions) => SsoFlow | undefined;
}

const POPUP_FEATURES = 'width=800,height=600';
const POPUP_POLL_INTERVAL_MS = 1000;

/**
 * Runs SSO in the current view, which replaces the application.
 *
 * @param redirectUrl - Identity provider URL to navigate to.
 * @returns A flow that can only redirect, since there is nothing to close.
 *
 * @example
 * redirectCurrentView('https://idp.example.com/authorize');
 */
const redirectCurrentView = (redirectUrl: string): SsoFlow => {
    window.location.href = redirectUrl;
    return {
        redirect: url => {
            window.location.href = url;
        },
        close: () => undefined,
    };
};

/**
 * Runs SSO in a popup and resolves it through a same-origin `postMessage`.
 *
 * @param options - Identity provider URL and outcome callbacks.
 * @returns The live flow, or `undefined` when the popup was blocked.
 *
 * @example
 * startPopupFlow(options);
 */
const startPopupFlow = ({
    redirectUrl,
    onResult,
    onAbandon,
}: SsoFlowOptions): SsoFlow | undefined => {
    const popup = window.open('', '', POPUP_FEATURES);
    if (!popup || popup.closed) {
        return undefined;
    }

    const listeners = new AbortController();
    let pollingId: number | undefined;
    const stopListening = () => {
        if (pollingId !== undefined) {
            window.clearInterval(pollingId);
            pollingId = undefined;
        }
        listeners.abort();
    };

    if (redirectUrl) {
        popup.location.href = redirectUrl;
    }

    // The popup reports dismissal through no event the opener can observe.
    pollingId = window.setInterval(() => {
        if (popup.closed) {
            stopListening();
            onAbandon('cancelled');
        }
    }, POPUP_POLL_INTERVAL_MS);
    window.addEventListener(
        'message',
        (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }
            if (!isSSOdMessage(event.data)) {
                return;
            }
            stopListening();
            popup.close();
            onResult(event.data);
        },
        {signal: listeners.signal},
    );

    return {
        redirect: url => {
            popup.location.href = url;
        },
        close: () => {
            stopListening();
            popup.close();
        },
    };
};

/**
 * Creates the browser transport, which runs SSO in a same-origin popup and
 * receives the verdict through `postMessage`.
 *
 * @returns A transport backed by `window.open`.
 *
 * @example
 * createBrowserSsoTransport().start(options);
 */
const createBrowserSsoTransport = (): SsoTransport => ({
    start: options =>
        options.redirectUrl && options.preferCurrentView
            ? redirectCurrentView(options.redirectUrl)
            : startPopupFlow(options),
});

let currentSsoTransport: SsoTransport = createBrowserSsoTransport();

/**
 * Replaces the SSO transport used by the application.
 *
 * @param transport - Host-specific transport, or `undefined` to restore the
 * browser popup flow.
 *
 * @example
 * setSsoTransport(createSsoTransport());
 */
export const setSsoTransport = (transport: SsoTransport | undefined): void => {
    currentSsoTransport = transport ?? createBrowserSsoTransport();
};

/**
 * Starts an SSO flow through the currently configured transport.
 *
 * @param options - Identity provider URL, surface preference, and outcome
 * callbacks.
 * @returns The live flow, or `undefined` when the host refused to start it.
 *
 * @example
 * const flow = startSsoFlow({
 *     redirectUrl,
 *     preferCurrentView: false,
 *     onResult,
 *     onAbandon,
 * });
 */
export const startSsoFlow = (options: SsoFlowOptions): SsoFlow | undefined =>
    currentSsoTransport.start(options);
